import React, { ReactNode, createContext, forwardRef, useContext, useState } from "react";

import { Alert as MUIAlert, Slide, SlideProps, Snackbar } from "@mui/material";

const SlideTransition = forwardRef<HTMLDivElement, SlideProps>((props, ref) => (
  <Slide {...props} direction="down" ref={ref} />
));

type AlertContextType = {
  alert: {
    success: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
    error: (message: string) => void;
  };
};

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [variant, setVariant] = useState<"success" | "info" | "warning" | "error">("info");
  const [alertCount, setAlertCount] = useState(0);

  const showMessage = (message: string, variant: "success" | "info" | "warning" | "error") => {
    setMessage(message);
    setVariant(variant);
    setOpen(true);
    setAlertCount((count) => count + 1);
  };

  const alert = {
    success: (message: string) => showMessage(message, "success"),
    info: (message: string) => showMessage(message, "info"),
    warning: (message: string) => showMessage(message, "warning"),
    error: (message: string) => showMessage(message, "error"),
  };

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <AlertContext.Provider value={{ alert }}>
      {children}
      <Snackbar
        key={alertCount}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideTransition}
      >
        <MUIAlert onClose={handleClose} severity={variant} variant="filled" sx={{ whiteSpace: "pre-line" }}>
          {message}
        </MUIAlert>
      </Snackbar>
    </AlertContext.Provider>
  );
};
